'use client';

import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { parseCookies, setCookie } from 'nookies';
import { useIntl, defineMessages } from 'react-intl';

import { useConfig } from '@packages/utilities';
import { Dialog } from '@packages/shared/src/components/Dialog';
import { Button } from '@packages/shared/src/components/Button';
import { Box } from '@packages/shared/src/components/Box';
import { Image } from '@packages/shared/src/components/Image';
import { useDeviceType, useUrl } from '@packages/shared/src/hooks';
import { Typography } from '@packages/shared/src/components/Typography';
import { Link } from '@packages/shared/src/components/Link';
import { useTracking } from '@packages/tracking';
import { useSearchParams } from 'next/navigation';

export const REDIRECT_OVERLAY_COOKIE = 'redirectOverlay';

const {
  title,
  headline,
  description,
  bulletOne,
  bulletTwo,
  bulletThree,
  bulletFour,
  moreInformation,
  continueButton,
} = defineMessages({
  title: {
    id: 'redirectOverlay.title',
    defaultMessage: 'Tschüss Quelle – Servus Universal!',
  },
  headline: {
    id: 'redirectOverlay.headline',
    defaultMessage: 'Seit 01. Juni 2023 sagen wir Tschüss Quelle – Servus Universal',
  },
  description: {
    id: 'redirectOverlay.description',
    defaultMessage: 'Und das bedeutet: Alle Vorteile bleiben – und vieles wird noch besser!',
  },
  bulletOne: {
    id: 'redirectOverlay.bulletOne',
    defaultMessage: 'Das volle Quelle.at Sortiment und noch viel mehr',
  },
  bulletTwo: {
    id: 'redirectOverlay.bulletTwo',
    defaultMessage:
      'Regionalität ist für Universal als Österreichischer Online-Generalist ein wichtiges Thema',
  },
  bulletThree: {
    id: 'redirectOverlay.bulletThree',
    defaultMessage: 'Universal bietet Ihnen die XXL-Garantie',
  },
  bulletFour: {
    id: 'redirectOverlay.bulletFour',
    defaultMessage: 'Universal ist Partner bei jö, Österreichs größtem Bonus Club',
  },
  moreInformation: {
    id: 'redirectOverlay.moreInformation',
    defaultMessage: 'Mehr Infos finden Sie <underline>hier</underline>.',
  },
  continueButton: {
    id: 'redirectOverlay.continueButton',
    defaultMessage: 'Weiter auf universal.at shoppen',
  },
});

const onDialogClose = (
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  cookies: { [key: string]: string },
) => {
  setIsOpen(false);
  setCookie(
    null,
    REDIRECT_OVERLAY_COOKIE,
    `${parseInt(cookies[REDIRECT_OVERLAY_COOKIE], 10) + 1}`,
    { path: '/' },
  );
};

/**
 * RedirectOverlay component displayed if the current URL contains the defined redirect parameter.
 * */
export const RedirectOverlay: FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const cookies = parseCookies();
  const { isMobile } = useDeviceType();
  const searchParams = useSearchParams();
  const { formatMessage } = useIntl();
  const {
    redirectOverlay: {
      redirectLp: { de: lpDe },
      redirectParam,
    },
  } = useConfig();

  const queryParam = searchParams?.get(redirectParam);
  const asPath = useUrl();
  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    if (
      cookies[REDIRECT_OVERLAY_COOKIE] &&
      parseInt(cookies[REDIRECT_OVERLAY_COOKIE], 10) < 3 &&
      typeof window !== 'undefined' &&
      typeof queryParam !== 'undefined' &&
      asPath.indexOf(lpDe) === -1
    ) {
      setIsOpen(true);
      dispatchGTMEvent({ event: 'qatrdir' });
    } else if (
      !cookies[REDIRECT_OVERLAY_COOKIE] &&
      typeof window !== 'undefined' &&
      typeof queryParam !== 'undefined' &&
      asPath.indexOf(lpDe) === -1
    ) {
      setCookie(null, REDIRECT_OVERLAY_COOKIE, '0', { path: '/' });
      setIsOpen(true);
      dispatchGTMEvent({ event: 'qatrdir' });
    }
    // cookies need to be excluded from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath, lpDe, queryParam, redirectParam]);

  return (
    <Dialog
      open={isOpen}
      dialogActions={
        <Button onClick={() => onDialogClose(setIsOpen, cookies)} color="primary">
          {formatMessage(continueButton)}
        </Button>
      }
      handleClose={() => onDialogClose(setIsOpen, cookies)}
      title={formatMessage(title)}
      dialogContent={
        <>
          <Box
            sx={{
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '12.75rem',
              position: 'relative',
            }}
          >
            <Image
              src="/search-assets/QAT2UVS.svg"
              style={{ position: 'absolute', height: '100%' }}
              width={768}
              height={204}
              alt=""
              loading="lazy"
              unoptimized
            />
          </Box>
          <Box
            sx={{
              width: ' 100%',
              maxWidth: isMobile ? '21.5rem' : 'initial',
              margin: '0 auto',
              padding: '0 1rem',
            }}
          >
            <Typography variant="h4" sx={{ margin: '1.5rem 0' }}>
              {formatMessage(headline)}
            </Typography>
            <Typography variant="body1">{formatMessage(description)}</Typography>
            <ul>
              <li>
                <Typography variant="body1">{formatMessage(bulletOne)}</Typography>
              </li>
              <li>
                <Typography variant="body1">{formatMessage(bulletTwo)}</Typography>
              </li>
              <li>
                <Typography variant="body1">{formatMessage(bulletThree)}</Typography>
              </li>
              <li>
                <Typography variant="body1">{formatMessage(bulletFour)}</Typography>
              </li>
            </ul>
            <Typography variant="body1">
              {formatMessage(moreInformation, {
                underline: (msg: ReactNode) => <Link href={lpDe}>{msg}</Link>,
              })}
            </Typography>
          </Box>
        </>
      }
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default RedirectOverlay;
